import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // Fetch fresh user data from backend
  const fetchUserData = useCallback(async (authToken) => {
    try {
      console.log('[Auth] Fetching user data with token...');
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      console.log('[Auth] User data received:', res.data);
      console.log('[Auth] is_admin value:', res.data.is_admin);
      return res.data;
    } catch (e) {
      console.error('[Auth] Failed to fetch user data:', e);
      return null;
    }
  }, []);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initAuth = async () => {
      console.log('[Auth] Initializing...');
      const savedToken = localStorage.getItem('arena_token');
      
      if (savedToken) {
        console.log('[Auth] Found saved token, validating...');
        const userData = await fetchUserData(savedToken);
        
        if (userData) {
          console.log('[Auth] Token valid, user loaded:', userData.display_name, 'isAdmin:', userData.is_admin);
          setToken(savedToken);
          setUser(userData);
        } else {
          console.log('[Auth] Token invalid, clearing...');
          localStorage.removeItem('arena_token');
        }
      } else {
        console.log('[Auth] No saved token found');
      }
      
      setLoading(false);
      setInitialized(true);
      console.log('[Auth] Initialization complete');
    };
    
    initAuth();
  }, [fetchUserData]);

  // Login function - stores token and user data
  const login = useCallback((newToken, userData) => {
    console.log('[Auth] Login called with user:', userData?.display_name, 'isAdmin:', userData?.is_admin);
    localStorage.setItem('arena_token', newToken);
    setToken(newToken);
    setUser(userData);
  }, []);

  // Logout function - clears all auth state
  const logout = useCallback(() => {
    console.log('[Auth] Logout called');
    localStorage.removeItem('arena_token');
    setToken(null);
    setUser(null);
  }, []);

  // Refresh user data - useful after admin changes
  const refreshUser = useCallback(async () => {
    if (token) {
      console.log('[Auth] Refreshing user data...');
      const userData = await fetchUserData(token);
      if (userData) {
        setUser(userData);
        console.log('[Auth] User data refreshed');
      }
    }
  }, [token, fetchUserData]);

  // Compute derived values
  const isAuthenticated = !!user && !!token;
  const isAdmin = user?.is_admin === true;

  console.log('[Auth] Current state - authenticated:', isAuthenticated, 'isAdmin:', isAdmin, 'loading:', loading);

  const value = {
    user,
    token,
    loading,
    initialized,
    login,
    logout,
    refreshUser,
    isAuthenticated,
    isAdmin
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
